import {useEffect} from "react";
import {Editor, Element, Transforms} from "slate";
import * as SlateReact from "slate-react";

import {useMathEditorContext} from "@repo/guide-math-editor-context";
import {RequestInfo} from "@repo/shared-core";

import {useSpeechRecognition} from "../use-speech-recognition.ts";

export const useSpeechToText = () => {
    const editor = SlateReact.useSlate();
    const isMobile = RequestInfo.isMobile;
    const speech = useSpeechRecognition();
    const showMicButton = !isMobile && speech.isSupportedBrowser;
    const {isAnyActive: isAnyMathActive} = useMathEditorContext();

    // Effect to handle when a math input becomes active while speech
    // recognition is on.
    useEffect(() => {
        // If the speech recognition is on, then we want to disable the
        // speech-to-text for the math editor.
        if (isAnyMathActive && speech.on) {
            speech.toggleSpeechRecognition();
        }
    }, [isAnyMathActive, speech]);

    // Effect to handle speech-to-text final transcription results
    useEffect(() => {
        // The last transcription represents the result of transcribing a
        // continuous stream of speech. We want to insert this into the editor
        // at the current selection point.
        if (speech.lastTranscription) {
            editor.insertText(speech.lastTranscription);
        }
    }, [editor, speech.lastTranscription]);

    // Effect to handle intermediary speech-to-text transcription results.
    // This is used to update the editor with the current transcription as
    // the user is speaking. This is not the final transcription result and
    // will eventually be replaced by the final result.
    useEffect(() => {
        if (speech.interimTranscription) {
            // We'll insert the interim transcription at the current selection
            // point. If the user is currently in a math input, then we'll
            // set the current selection to the end of the editor to avoid
            // breaking the math input.
            let startPoint = editor.selection?.anchor ?? Editor.end(editor, []);
            const [node] = Editor.above(editor, {at: startPoint}) ?? [];

            if (Element.isElement(node) && node.type === "math-editor") {
                startPoint = Editor.end(editor, []);
            }
            editor.insertText(speech.interimTranscription);
            // We'll update the current selection so that the next interim
            // transcription or final result will replace the text that was
            // just inserted.
            Transforms.setSelection(editor, {
                anchor: startPoint,
                focus: editor.selection?.focus ?? Editor.end(editor, []),
            });
        }
    }, [editor, speech.interimTranscription]);

    return {
        speechInProgress: speech.on && speech.transcribing,
        showMicButton,
        speech,
    };
};
